// CLIENT PORTAL §A — the group charts' DRAWING decisions (a pure leaf).
//
// `groupSeries.ts` answers "what was measured" and is UNTOUCHED by this rewrite;
// this answers "what is drawn, in what colour, on which axis, and what must be
// withheld". Read this header before touching any of it.
//
// ⚠️⚠️ GROUPED VOLUME BARS ON THE RIGHT AXIS, A COST LINE PER CLUB ON THE LEFT.
// Round 4 drew indexed lines on one unitless axis; this is the chart Scott's
// clients already read, with every location on it. `indexSeries` and the whole
// index model are DELETED — the two-indices-against-one-reference-line defect
// went with them, and there is nothing left importing either.
//
// ⚠️⚠️ THE RULE THAT WAS NARROWED, AND SHOULD HAVE BEEN NARROW FROM THE START:
// "never a second y-axis" exists because a reader infers a relationship from two
// LINES crossing, when the axes' relative scaling is an arbitrary choice.
// Bars-vs-line does not have that failure mode — a bar crossing a line cannot be
// misread as a crossing of like things, and the mark difference tells the reader
// which axis it belongs to without consulting the legend. The rule is now:
// NEVER TWO Y-AXES FOR TWO SERIES OF THE SAME MARK TYPE. What actually disarms
// this one is that BOTH AXES ARE ZERO-BASED — the classic dual-axis lie is
// manufactured by floating baselines, not by the second axis itself.
//
// ⚠️⚠️ CURRENCY IS A BLOCKER, NOT HOUSEKEEPING. A cost axis is money. The
// indexed chart hid mixed currency because an index is unitless, and that
// accidental protection is gone. See `CostLayer` — the axis is not
// CONSTRUCTIBLE without a resolved single currency, so an edit that forgets the
// check fails `tsc` rather than drawing a blended money axis.

import type {
  PortalGroupClub,
  PortalGroupClubPoint,
  PortalGroupSeries,
  PortalReportLabels,
} from '../../types/portalReport.js';
import { disambiguatedCurrencySymbol } from './currencySymbol.js';

/**
 * ⚠️⚠️ THE VALIDATED CATEGORICAL RAMP. Eight slots, two modes, generated against
 * the constraints and checked with a runnable six-check validator (lightness
 * band, chroma floor, CVD adjacent-pair separation, normal-vision floor,
 * contrast vs surface) — ALL PASS on every portal surface in both modes.
 * Worst adjacent CVD ΔE 10.0 light / 9.6 dark against a target of 8; worst
 * normal-vision ΔE 20.9 / 21.4 against a floor of 15.
 *
 * ⚠️ NOT EYEBALLED, AND NOT TO BE EDITED BY EYE. Re-run
 * `scripts/validate_palette.js` in the dataviz skill on any change to a slot OR
 * to the ORDER — the adjacent-pair checks depend on the order.
 *
 * ⚠️⚠️ A BAR AT REDUCED ALPHA IS A NEW SURFACE and the ramp was never validated
 * at one. See `BAR_FILL_ALPHA`.
 *
 * ⚠️⚠️ NO GREEN AND NO VOLT, DELIBERATELY. Volt is the page's single emphasis
 * element, spent on the hero figure; kelly is `--rpt-fig`, the figure colour, so
 * a green line would read as "this club is the total". Green means FIGURE on
 * this report, never CLUB.
 *
 * ⚠️ Slot 1 is the same blue as `--rpt-marine`, which means "Google" elsewhere
 * on this report. ACCEPTED KNOWINGLY: within one chart there is no ambiguity
 * (the club charts carry no channel encoding), and moving it would require
 * re-validating the whole order for a collision that never occurs inside a
 * single frame.
 */
export const CLUB_PALETTE_LIGHT = [
  '#1f7fa6',
  '#c43a76',
  '#8f6b00',
  '#7d4bb5',
  '#bd5c14',
  '#0e8f9f',
  '#c0392b',
  '#3b5fd0',
] as const;

export const CLUB_PALETTE_DARK = [
  '#2f93bb',
  '#d4548a',
  '#b98b0e',
  '#8f60c9',
  '#d2691e',
  '#12a3b5',
  '#d4483a',
  '#5480e0',
] as const;

/** ⚠️ A NINTH CLUB IS NOT A NINTH COLOUR. See `rankClubsForCharts`. */
export const MAX_CHART_CLUBS = CLUB_PALETTE_LIGHT.length;

/**
 * How many clubs open ticked.
 *
 * ⚠️ THE CAP, deliberately — every location the client holds, up to the size of
 * the validated ramp. Round 4 opened on three because sixteen indexed lines were
 * unreadable; grouped bars carry eight far better, and a group owner opening
 * their report wants the whole group, not a sample of it.
 */
export const DEFAULT_SELECTED_CLUBS = MAX_CHART_CLUBS;

/**
 * ⚠️⚠️ THE BARS ARE SOLID, AND THAT IS A MEASURED REVERSAL — NOT AN OVERSIGHT.
 *
 * The plan was "bars recede, lines lead" done with a muted fill: the ramp at
 * reduced alpha over the card. A bar at reduced alpha is a NEW SURFACE the
 * validator never saw, so it was measured in a browser at the density case —
 * eight clubs, thirteen months, both modes — and it FAILED:
 *
 *   55% over `--rpt-card`  →  1.84–2.48 dark, 1.83–2.22 light  (floor 3:1)
 *   the same ramp SOLID     →  3.37 dark, 3.01 light            (passes)
 *
 * ⚠️ So the fallback the brief named is what shipped: keep the bars solid and
 * lighten the GRID instead. Figure and ground is then done with WEIGHT and a
 * HALO on the lines (`LINE_STROKE`, `LINE_HALO`) rather than by weakening the
 * bars — a line at 3px sitting on a card-coloured 6px halo reads above 104 bars
 * without either mark dropping below the contrast floor.
 *
 * ⚠️⚠️ DO NOT REINTRODUCE AN ALPHA WITHOUT RE-MEASURING. The numbers above are
 * what a browser reported, and jsdom cannot compute them — this whole class of
 * defect is invisible to the suite. There is deliberately NO alpha constant left
 * to reach for: a `BAR_FILL_ALPHA = 1` would be a dead multiply that reads as
 * live, and the guard is `costAxisUnforgeable.test.ts`'s assertion that the
 * chart contains no `rgba(` at all.
 */

/**
 * ⚠️ THE GRID IS THE THING THAT RECEDES. Gridlines are not data, so they are not
 * held to the 3:1 non-text floor the marks are — which is exactly why they are
 * the right thing to weaken when the marks cannot be.
 */
export const GRID_OPACITY = 0.45;

/** Lines lead: a heavier stroke than the default, over a card-coloured halo. */
/**
 * ⚠️⚠️ THINNER THAN ROUND 5 (§4b, 2026-09-03), AND THE HALO SHRANK WITH IT.
 * At eight clubs the lines were heavy enough to become the chart's texture
 * rather than its figure — 3 units of stroke over 6 of halo is 4.5 units of
 * occlusion per line, and eight of those across thirteen months buries the bars
 * they are supposed to lead.
 *
 * ⚠️⚠️ THE HALO IS NOT INDEPENDENT. It is the stroke plus one unit of card
 * colour on EACH side, so it MUST move when the stroke does; left at 6 over a
 * 2-unit stroke it would be a 2-unit cream outline round a 2-unit line, which
 * reads as a white line with a coloured core.
 *
 * ⚠️⚠️ MEASURED IN A BROWSER, AND THE FIRST DRAFT OF THIS NOTE HAD THE SCALE
 * BACKWARDS. These are viewBox units on a 900-unit viewBox, and the chart
 * renders WIDER than that, not narrower: 912px at a 1024 viewport (scale 1.01)
 * and 1284px at 1440 (scale 1.43). So the stroke renders at 2.03px and 2.85px,
 * and the halo at 4.05px and 5.71px — down from 3.04/4.28 and 6.08/8.56.
 *
 * ⚠️⚠️ WHICH MEANS A UNIT WIDTH HAS NO FIXED RENDERED WIDTH, and picking
 * units to hit "2 rendered px" only works at one viewport. 2 units is chosen
 * because it is 2.0px at the NARROW end and still only 2.9px at 1440; 1.5 units
 * would sit exactly on the ~1.5px floor at which six adjacent ramp colours stop
 * being traceable. Do not thin them further without measuring at eight clubs at
 * BOTH widths; the ramp's separation was validated at full weight.
 */
export const LINE_STROKE = 2;
export const LINE_HALO = 4;

/**
 * ⚠️⚠️ TWO CHARTS SINCE §4e (2026-09-03). The third was Ad Conversions, and
 * Scott cut it: the leaderboard already carries that figure, and a third chart
 * of the same shape made the band a wall.
 *
 * ⚠️⚠️ REMOVING IT CANNOT RENUMBER THE RAMP, and that is a property of where
 * the index comes from rather than of care taken here: `colorIndex` is a club's
 * position in `rankClubsForCharts`, which is ranked over the CLUBS and knows
 * nothing about charts. A club keeps its colour on every chart and across the
 * removal of any of them. Asserted, because it is the kind of thing that would
 * go unnoticed for a release.
 */
export type GroupChartKey = 'internetLeads' | 'sales';

export interface GroupChartDef {
  key: GroupChartKey;
  /** ⚠️ TRUE for the two CRM charts, which are ABSENT for a scope with no CRM. */
  crm: boolean;
  title: string;
  volumeLabel: string;
  costLabel: string;
  /** The absolute count this chart's BARS draw. */
  volumeOf: (p: PortalGroupClubPoint) => number | null;
}

/**
 * The three charts, in order.
 *
 * ⚠️ Chart 3's volume is the SAME figure the leaderboard calls "Ad Conversions",
 * under the same name. One number, one name, everywhere on the report — so the
 * label comes from the shared label registry rather than being written here.
 */
export function groupChartDefs(labels: PortalReportLabels): GroupChartDef[] {
  return [
    {
      key: 'internetLeads',
      crm: true,
      title: 'Internet leads',
      volumeLabel: 'Internet leads',
      costLabel: 'Cost per internet lead',
      volumeOf: (p) => p.internetLeads,
    },
    {
      key: 'sales',
      crm: true,
      // ⚠️ FROM THE REGISTRY, not a literal. The defaults are the same words, so
      // nothing moves on screen — but a brand that counts "members" renames
      // this chart along with every other surface, which is what the registry
      // is for. It is also what keeps `labels` used now the Ad Conversions
      // chart (the only reader of `labels.leads` here) is gone.
      title: labels.sales,
      volumeLabel: labels.sales,
      costLabel: labels.costPerSale,
      volumeOf: (p) => p.sales,
    },
  ];
}

/**
 * ⚠️⚠️ THE STABLE CLUB ORDER, AND THEREFORE THE COLOUR ASSIGNMENT.
 *
 * Ordered by the club's TOTAL VOLUME over the period, tie-broken by account id
 * so it is deterministic. Colour then follows the club's position in THIS list,
 * which does not depend on what the reader has ticked — so unticking Midland
 * cannot repaint Kalgoorlie.
 *
 * ⚠️⚠️ ONLY THE FIRST EIGHT ARE DRAWABLE. A ninth club is not a ninth colour: a
 * generated hue is indistinguishable under CVD and breaks the validated set. The
 * remainder are RETURNED AND NAMED rather than dropped — see `excluded` — so the
 * page can say which clubs are not on the chart and why, and their figures stay
 * reachable on the leaderboard and on their own report.
 */
export function rankClubsForCharts(clubs: readonly PortalGroupClub[]): {
  drawable: PortalGroupClub[];
  excluded: PortalGroupClub[];
} {
  const ordered = [...clubs].sort(
    (a, b) => b.volumeRank - a.volumeRank || a.accountId.localeCompare(b.accountId),
  );
  return {
    drawable: ordered.slice(0, MAX_CHART_CLUBS),
    excluded: ordered.slice(MAX_CHART_CLUBS),
  };
}

/** The clubs ticked when the page opens. */
export function defaultSelectedClubIds(drawable: readonly PortalGroupClub[]): string[] {
  return drawable.slice(0, DEFAULT_SELECTED_CLUBS).map((c) => c.accountId);
}

/**
 * A club's cost series over a chart's volume metric.
 *
 * ⚠️ RE-DERIVED PER BUCKET from that bucket's own spend and count — never an
 * average of ratios, and never a ratio of averages. A bucket with spend and no
 * count is NULL rather than a plotted zero: a cost per lead of $0 reads as
 * "leads were free" when the truth is money spent for nothing, which is the most
 * flattering misstatement available on a client report.
 */
export function costValues(
  points: readonly PortalGroupClubPoint[],
  volumeOf: (p: PortalGroupClubPoint) => number | null,
  /**
   * ⚠️⚠️ THE PARTIAL FINAL BUCKET CONTRIBUTES NO COST POINT (§4a, 2026-09-03).
   *
   * A month in progress has most of its spend and only some of its conversions,
   * so its cost per lead is inflated — often several times over. Measured on a
   * live report the left axis ran 0 to over 100 while every complete month sat
   * between $4 and $25: ONE partial point was stretching the axis about fourfold
   * and flattening the entire year into the bottom eighth of the plot.
   *
   * ⚠️⚠️ EXCLUDED FROM THE LINE, NOT MERELY FROM THE DOMAIN. Nulling the value
   * does both, because the axis is built from these same values — and a point
   * drawn outside a clamped domain would be worse than either. The BARS keep
   * their partial month, hatched: a count of a period in progress is a real
   * partial count, where a RATIO over a partial period is not a partial ratio,
   * it is a wrong one.
   */
  excludeFinalBucket = false,
): Array<number | null> {
  return points.map((p, i) => {
    if (excludeFinalBucket && i === points.length - 1) return null;
    const count = volumeOf(p);
    if (p.spend === null || count === null || count === 0) return null;
    return p.spend / count;
  });
}

// ---------------------------------------------------------------------------
// Axes
// ---------------------------------------------------------------------------

export interface AxisScale {
  /**
   * ⚠️⚠️ ALWAYS 0, AND THIS IS THE BAR RULE. A bar's LENGTH encodes its value, so
   * a floating baseline makes a 4% difference look like a doubling. The literal
   * type is what makes that unforgeable.
   *
   * ⚠️ THE COST AXIS IS A DIFFERENT TYPE (`FittedAxis`) because it carries only
   * LINES, whose POSITION encodes value — see §5. Widening THIS `min` to
   * `number` to serve both would relax the bar rule as a side effect, which is
   * exactly what `costAxisUnforgeable.test.ts` refuses.
   */
  readonly min: 0;
  readonly max: number;
  readonly ticks: readonly number[];
}

/**
 * A zero-based axis over a set of values.
 *
 * ⚠️⚠️ THE MAX IS THE LARGEST SINGLE VALUE, NEVER A SUM. The bars are GROUPED,
 * one per club per month — a stacked reading would be a different chart, and an
 * axis sized to the sum would squash every bar into the bottom eighth of the
 * plot while implying the clubs add up to something.
 *
 * ⚠️ `min` is typed as the literal `0`, so an axis with a floating baseline is
 * not constructible. That is the half of the dual-axis protection that has to
 * hold; the other half is that neither axis is ever drawn without the other's
 * mark type differing.
 */
export function zeroBasedAxis(values: ReadonlyArray<number | null>): AxisScale {
  const present = values.filter((v): v is number => v !== null && Number.isFinite(v));
  const peak = present.length > 0 ? Math.max(...present) : 0;
  // ⚠️ An all-zero (or empty) set still gets a real axis, so an empty chart draws
  // its frame rather than dividing by zero. 1 rather than 0 keeps the ticks
  // distinct.
  const max = niceCeiling(peak <= 0 ? 1 : peak);
  return { min: 0, max, ticks: [0, max / 2, max] };
}

/** Round up to a readable axis top: 1, 2, 2.5 or 5 × a power of ten. */
function niceCeiling(peak: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(peak));
  for (const step of [1, 2, 2.5, 5, 10]) {
    const candidate = step * magnitude;
    if (candidate >= peak) return candidate;
  }
  return 10 * magnitude;
}
/**
 * The next step up the same readable ladder: 1 — 2 — 2.5 — 5 — 10 — 20 — …
 *
 * ⚠️ USED TO WIDEN A FRAME THAT DOES NOT CONTAIN THE DATA, which is why it must
 * walk the SAME ladder `niceCeiling` uses: a widened axis whose step came off a
 * different ladder would print ticks in a precision the rest of the chart never
 * produces.
 */
function nextNiceStep(step: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(step));
  const mantissa = step / magnitude;
  for (const candidate of [1, 2, 2.5, 5]) {
    if (candidate > mantissa + 1e-9) return candidate * magnitude;
  }
  return 10 * magnitude;
}
/**
 * An axis whose baseline may sit ABOVE zero.
 *
 * ⚠️⚠️ ITS OWN TYPE, NOT A WIDENED `AxisScale`, and that is the whole guard. The
 * two axes on this chart are held to DIFFERENT rules, so they are different
 * types: relaxing `AxisScale.min` off the literal `0` to serve the cost axis
 * would silently permit a floating BAR baseline, which is the misreading the
 * zero rule exists to prevent (a bar's length encodes its value).
 *
 * ⚠️ `AxisScale` is assignable TO this (a `0` is a `number`), which is the safe
 * direction and is what lets the renderer's projector take one parameter type.
 * The reverse does not typecheck — asserted in `costAxisUnforgeable.test.ts`.
 */
export interface FittedAxis {
  readonly min: number;
  readonly max: number;
  readonly ticks: readonly number[];
}

/**
 * How much of the data's own spread to leave as breathing room, above and
 * below. 15% — enough that the extremes are not sitting on the frame, small
 * enough that the variation still fills the plot.
 */
const FIT_PADDING = 0.15;

/**
 * How many intervals the fitted axis may divide into. THREE CANDIDATES, TRIED
 * IN ORDER, and the tightest fit wins.
 *
 * ⚠️⚠️ A FIXED TWO INTERVALS IS WHY THE FIRST VERSION BARELY HELPED. Measured on
 * Scott's own case — eight clubs, cost per lead 9.4—26.6 — a two-interval axis
 * lands on 0—40 and the lines fill 43% of the frame, against 34% zero-based. The
 * step comes from a FIVE-VALUE nice set (1 · 2 · 2.5 · 5 · 10), so with only two
 * intervals to spend it overshoots badly. Three intervals gives 0—30 and 57%.
 *
 * ⚠️ CAPPED AT FOUR (five labels) rather than opened up: the left gutter is
 * narrow, and past that the labels crowd. Fewer intervals wins a near-tie
 * (`FIT_TIEBREAK`), because fewer labels is the cleaner axis when the fit is
 * the same.
 *
 * ⚠️ SAFE ONLY BECAUSE THE COST TICKS ARE LABELS, NOT GRIDLINES. The grid comes
 * from the VOLUME axis alone (`GroupBarChart`: ONE grid, not two), so these need
 * not align with anything — if that ever changes, this cap becomes a constraint
 * rather than a preference.
 */
const FIT_INTERVALS = [2, 3, 4] as const;

/**
 * How much better a candidate must fill the frame to justify more labels.
 * Two percentage points — a real improvement, not a rounding difference.
 */
const FIT_TIEBREAK = 0.02;

/**
 * How many times a candidate's step may be widened to fit the data.
 *
 * ⚠️ A BOUND ON A LOOP IN A RENDER PATH, not a tuning knob. Two or three passes
 * is the observed worst case; the bound exists so a mistake in that reasoning
 * costs a dropped candidate rather than a frozen tab.
 */
const FIT_MAX_WIDENINGS = 12;

/**
 * An axis fitted to the data, for a set of LINES.
 *
 * ⚠️⚠️ THE MARK TYPE DECIDES THE BASELINE, and the rule this replaced was a bar
 * rule applied to lines. A line's POSITION encodes its value, so a fitted
 * baseline lies about nothing — while a zero floor hides the variation the chart
 * exists to show. MEASURED on a live render: the cost axis ran 0—50 against a
 * ~26 peak, so roughly half the frame was empty and every line sat in the lower
 * third.
 *
 * ⚠️ THE FLOOR IS ALWAYS LABELLED — `ticks[0]` IS `min`, and the renderer prints
 * the ticks verbatim, so a reader can see the baseline is not zero. A fitted
 * baseline that is not stated IS the misleading chart.
 *
 * ⚠️ ONE STEP FROM ZERO IS ZERO. A baseline one step up buys almost no vertical
 * room and still costs the reader a moment's doubt about the scale, so the
 * fitted baseline is spent only where it actually buys something.
 *
 * ⚠️⚠️ THAT SNAP IS A TRADE, NOT A FREE RULE, AND IT IS RECORDED RATHER THAN
 * DISCOVERED LATER. It PREFERS A ZERO BASELINE, which on a client-facing money
 * chart is the safer reading — and it sometimes costs fill to get one. Measured:
 * `[4.5, 10.25]` snaps to 0—15 and fills 38%, where an unsnapped floor would give
 * 2.5—12.5 and 57%.
 *
 * ⚠️ THE ALTERNATIVE, IF THAT TRADE IS EVER JUDGED WRONG, is to drop the snap and
 * evaluate TWO candidates per interval count — natural floor and forced zero — with
 * zero enumerated FIRST so it wins any near-tie by the existing `FIT_TIEBREAK`.
 * Prototyped and measured over the same fixtures: identical on Scott's
 * eight-club case (57%), better on `[4.5, 10.25]` (57%), zero containment
 * failures, five labels maximum, and a zero baseline still chosen 55% of the
 * time. It also removes the snap heuristic entirely, since "prefer zero" becomes
 * one ordering rule rather than a second mechanism.
 *
 * ⚠️ NOT TAKEN NOW because it changes the flat-series frame (10—14 becomes 0—20)
 * and reworks the tests that pin the snap, for no gain on the case this section
 * was opened to fix. A deliberate stop, not an oversight.
 *
 * ⚠️ A RELATED INTERACTION IS UNREACHABLE TODAY AND WOULD NOT BE IF
 * `FIT_INTERVALS` GAINED A 5: there the snap fires on a floor that equals the
 * step, forces a widening, and lands WORSE than the four-interval answer
 * (0—50 at 34%, against 0—30 at 57%). Adopt the alternative above before adding
 * a fifth interval.
 */
export function fittedAxis(values: ReadonlyArray<number | null>): FittedAxis {
  const present = values.filter((v): v is number => v !== null && Number.isFinite(v));
  // ⚠️ An empty set still gets a real frame, as `zeroBasedAxis` does — an empty
  // chart draws its axes rather than dividing by zero.
  if (present.length === 0) return { min: 0, max: 1, ticks: [0, 0.5, 1] };

  const lo = Math.min(...present);
  const hi = Math.max(...present);
  // ⚠️ A flat (or all-zero) series: pad around the value itself.
  const spread = hi - lo > 0 ? hi - lo : Math.max(Math.abs(hi), 1);
  const pad = spread * FIT_PADDING;

  // The window we would LIKE to cover: the data plus its padding, never below
  // zero. The DATA is what must end up inside the frame; the padding is a
  // preference the candidates below may trade away.
  const wantLo = Math.max(0, lo - pad);
  const wantHi = hi + pad;

  let best: FittedAxis | null = null;
  let bestFill = -1;

  for (const intervals of FIT_INTERVALS) {
    // ⚠️⚠️ THE DATA MUST BE INSIDE THE FRAME, AND THE SNAP CAN TAKE IT OUTSIDE.
    // Snapping the floor to zero lowers the TOP by the same amount, so a series
    // whose padded floor sat exactly one step up loses a step of headroom and its
    // peak is drawn off the plot. MEASURED, not theorised: it fires for cost per
    // lead 14.31—29.69 at two intervals (floor 10 = step 10, snapped to 0, top 20
    // against a 29.69 peak), and for 75,226 (lo, hi) pairs in a swept grid.
    //
    // ⚠️⚠️ THE REPAIR IS A BIGGER STEP, NEVER MORE INTERVALS, and the first draft
    // got that wrong. Adding intervals until the peak fits is unbounded: measured,
    // `[4.5, 10.25]` produced a SEVEN-LABEL axis, blowing straight through the cap
    // `FIT_INTERVALS` exists to impose, on the narrowest gutter on the page. Walking
    // the step up the same readable ladder keeps the label count at exactly
    // `intervals + 1` and fits BETTER: the 14.31—29.69 case goes from 0—30 to
    // 10—30, i.e. 31% of the frame to 77%.
    let step = niceCeiling(Math.max((wantHi - wantLo) / intervals, Number.MIN_VALUE));
    let min = 0;
    let max = 0;
    // ⚠️ BOUNDED. Each widening raises `max` (the floor can only fall), so this
    // terminates in two or three passes — but an unbounded `while` in a render
    // path is a hang if that reasoning is ever wrong, and a chart that fails to
    // fit is recoverable where a frozen tab is not.
    for (let widening = 0; widening < FIT_MAX_WIDENINGS; widening += 1) {
      // ⚠️ Cost per lead is never negative, so the floor never goes below zero — a
      // negative money axis would be a category error, not a tighter fit.
      //
      // ⚠️⚠️ THIS `Math.max(0, …)` IS BELT-AND-BRACES TODAY, NOT LOAD-BEARING,
      // and saying so is the point: the snap below returns 0 for ANY floor at or
      // below one step, and a negative value satisfies that trivially — so the
      // clamp can never be the deciding rule while the snap is written that way.
      // Found by mutation: removing it fails nothing. The same shape as
      // `costAxisUnforgeable.test.ts`'s own lesson (two guards on one expression
      // means only the stronger is under test) and as the `align-self: flex-start`
      // note in index.css.
      //
      // ⚠️ IT STAYS. It costs nothing, and it is load-bearing again the moment the
      // snap threshold is narrowed — e.g. to `floored < step * 0.5` — which is a
      // plausible future tweak that would otherwise let a negative floor through
      // to a money axis.
      const floored = Math.max(0, Math.floor(wantLo / step) * step);
      min = floored <= step ? 0 : floored;
      max = min + intervals * step;
      if (max >= hi) break;
      step = nextNiceStep(step);
    }
    // ⚠️ A candidate that still does not fit is DROPPED rather than drawn.
    //
    // ⚠️⚠️ UNREACHABLE TODAY, AND IT IS THE PARTNER OF `FIT_MAX_WIDENINGS`. Never
    // reached in a swept grid of 0—120, and mutation-confirmed: removing it fails
    // nothing. It is what makes the BOUND above safe — the moment that bound is
    // too small for some future range, this is the difference between a narrower
    // candidate winning and a line drawn outside the plot. Kept for the same
    // reason as the `Math.max(0, ...)` clamp below, and stated rather than
    // pretended to be covered.
    if (max < hi) continue;

    // How much of the frame the DATA occupies. A flat series scores 0 on every
    // candidate, so the first (fewest labels) wins — which is right: there is no
    // spread to fit, and a flat line reads the same at any zoom.
    const fill = (hi - lo) / (max - min);
    if (fill > bestFill + FIT_TIEBREAK) {
      bestFill = fill;
      best = {
        min,
        max,
        // ⚠️ Built by INDEX rather than by repeated addition, so a fractional step
        // does not accumulate drift across four ticks.
        ticks: Array.from({ length: intervals + 1 }, (_, i) => min + i * step),
      };
    }
  }

  // ⚠️ `FIT_INTERVALS` is a non-empty literal and the first candidate always beats
  // `bestFill = -1`, so this is unreachable — it is here because TypeScript cannot
  // see that, and a non-null assertion would hide a real bug if the list were
  // ever emptied.
  if (best === null) return { min: 0, max: 1, ticks: [0, 0.5, 1] };
  return best;
}

// ---------------------------------------------------------------------------
// The cost layer — the branded one
// ---------------------------------------------------------------------------

/**
 * Nominal brand. Declared, never exported — a cost axis cannot be forged.
 *
 * ⚠️ The same device `PortalScope` uses, and for the same reason: the type is
 * the enforcement. A `declare`d unique symbol has NO RUNTIME VALUE, so the
 * object carries no extra key and the CAST inside `resolveCostLayer` is what
 * confers the brand.
 */
declare const COST_LAYER_BRAND: unique symbol;

export interface CostLine {
  accountId: string;
  name: string;
  /** The club's slot in the validated ramp. Its bars share it. */
  colorIndex: number;
  /** ⚠️ NULL = not measured. A break in the line, never a zero. */
  values: Array<number | null>;
}

/**
 * ⚠️⚠️ THE COST AXIS AND ITS LINES, AND THE ONLY WAY TO GET ONE IS
 * `resolveCostLayer`.
 *
 * A cost axis is MONEY. One shared left axis across two currencies is
 * meaningless — and the old indexed chart hid that, because an index is
 * unitless, so the protection was accidental and is now gone.
 *
 * ⚠️⚠️ THE BRAND IS THE POINT. The lines live INSIDE this object, so there is no
 * way to draw a cost line without holding a value that could only have come from
 * a resolved single currency. A future edit that forgets the check does not
 * compile; it cannot reach for the lines and skip the axis, because they are the
 * same object. A runtime `if` would have been the weaker version of this — it
 * can be deleted by one edit and nothing fails until a trans-Tasman group opens
 * their report.
 *
 * ⚠️ `currency` is therefore GUARANTEED to be the one currency every line on the
 * axis is denominated in, and `axisLabel` carries a symbol that says WHICH
 * dollar — see `disambiguatedCurrencySymbol`; `Intl` cannot do it from one
 * locale.
 */
export interface CostLayer {
  readonly currency: string;
  readonly axisLabel: string;
  readonly axis: FittedAxis;
  readonly lines: readonly CostLine[];
  readonly [COST_LAYER_BRAND]: true;
}

export interface CostResolution {
  /** ⚠️ NULL = no cost axis and no lines at all. The reason is below. */
  layer: CostLayer | null;
  /**
   * Why the WHOLE cost axis is withheld, when it is.
   *
   * ⚠️ The bars still draw in that case — volume is a count and has no currency,
   * so the chart is still worth drawing and the reader is told what is missing
   * rather than shown a blended money axis.
   */
  axisWithheldReason: string | null;
  /** Clubs whose OWN line is withheld though the axis exists. */
  clubsWithheld: Array<{ name: string; reason: string }>;
}

const MIXED_CLUB_REASON =
  'its spend came back in more than one currency, so its cost cannot be drawn on one axis';

/**
 * ⚠️⚠️ THE TICKED SET SPANNING TWO CURRENCIES WITHHOLDS THE WHOLE AXIS. This is
 * the rule that matters: a trans-Tasman group owner ticking an AU club and an NZ
 * club has no single money scale to draw either line on, and drawing them
 * anyway would put two currencies on one axis under one symbol.
 *
 * ⚠️ A SINGLE club with mixed currency loses only ITS line — `clubCurrency`
 * already returns null for that, and the axis survives on the rest.
 *
 * ⚠️ Both reasons are STATED, never left as a missing line. A reader ticked those
 * clubs, and a chart that silently ignores a tick reads as broken software.
 */
export function resolveCostLayer(
  clubs: readonly { club: PortalGroupClub; colorIndex: number }[],
  def: GroupChartDef,
  /** ⚠️ §4a — the month in progress contributes no cost point. See `costValues`. */
  excludeFinalBucket = false,
): CostResolution {
  const clubsWithheld: Array<{ name: string; reason: string }> = [];
  const usable: Array<{ club: PortalGroupClub; colorIndex: number; currency: string }> = [];
  for (const entry of clubs) {
    if (entry.club.currency === null) {
      clubsWithheld.push({ name: entry.club.name, reason: MIXED_CLUB_REASON });
      continue;
    }
    usable.push({ ...entry, currency: entry.club.currency });
  }

  const currencies = [...new Set(usable.map((u) => u.currency))];
  if (currencies.length > 1) {
    return {
      layer: null,
      axisWithheldReason: `The locations you have selected report in ${currencies.sort().join(' and ')}, and a single cost axis cannot carry two currencies. The ${def.costLabel.toLowerCase()} lines are not shown; the bars below are counts and are unaffected.`,
      clubsWithheld,
    };
  }
  const currency = currencies[0];
  if (!currency) {
    return {
      layer: null,
      axisWithheldReason: `No ${def.costLabel.toLowerCase()} can be shown for the locations you have selected.`,
      clubsWithheld,
    };
  }

  const lines: CostLine[] = usable.map((u) => ({
    accountId: u.club.accountId,
    name: u.club.name,
    colorIndex: u.colorIndex,
    values: costValues(u.club.points, def.volumeOf, excludeFinalBucket),
  }));

  return {
    // ⚠️ THE CAST IS THE BRAND — see `COST_LAYER_BRAND`. It is confined to this
    // one expression, which is reachable only past the single-currency check
    // above, so there is no other way anywhere in the codebase to obtain a value
    // of this type.
    layer: {
      currency,
      axisLabel: `${def.costLabel} (${disambiguatedCurrencySymbol(currency)})`,
      // ⚠️⚠️ FITTED, NOT ZERO-BASED — lines only, so position encodes value. The
      // volume axis below stays `zeroBasedAxis` because it carries BARS.
      axis: fittedAxis(lines.flatMap((l) => l.values)),
      lines,
    } as unknown as CostLayer,
    axisWithheldReason: null,
    clubsWithheld,
  };
}

// ---------------------------------------------------------------------------
// The chart model
// ---------------------------------------------------------------------------

export interface BarSeries {
  accountId: string;
  name: string;
  colorIndex: number;
  /** ⚠️ NULL = not measured. No bar at all, never a zero-height one. */
  values: Array<number | null>;
}

export interface GroupChartModel {
  key: GroupChartKey;
  title: string;
  volumeLabel: string;
  costLabel: string;
  /** Grouped volume bars, right axis. */
  bars: BarSeries[];
  volumeAxis: AxisScale;
  /** ⚠️ NULL when no single currency resolved — see `CostLayer`. */
  cost: CostLayer | null;
  costAxisWithheldReason: string | null;
  /** Clubs whose cost line is withheld, or which have nothing at all. */
  withheld: Array<{ name: string; reason: string }>;
}

/** Build one chart's drawable model from the payload and the ticked clubs. */
export function buildGroupChart(
  def: GroupChartDef,
  series: PortalGroupSeries,
  drawable: readonly PortalGroupClub[],
  selectedIds: ReadonlySet<string>,
): GroupChartModel {
  const selected: Array<{ club: PortalGroupClub; colorIndex: number }> = [];
  drawable.forEach((club, colorIndex) => {
    if (selectedIds.has(club.accountId)) selected.push({ club, colorIndex });
  });

  const bars: BarSeries[] = [];
  const withheld: Array<{ name: string; reason: string }> = [];
  for (const { club, colorIndex } of selected) {
    const values = club.points.map(def.volumeOf);
    // ⚠️⚠️ A CLUB WITH NOTHING MEASURED AT ALL IS NAMED, not silently absent.
    // The reader ticked it, and the round-4 rule survives the re-encoding: a
    // chart that quietly ignores a tick reads as broken.
    if (values.every((v) => v === null)) {
      withheld.push({
        name: club.name,
        reason: `no ${def.volumeLabel.toLowerCase()} were measured for it in this window`,
      });
      continue;
    }
    bars.push({ accountId: club.accountId, name: club.name, colorIndex, values });
  }

  const cost = resolveCostLayer(
    // ⚠️ Only the clubs that have bars — a club withheld entirely above has no
    // cost line to argue about either, and including it would let one club with
    // no data at all withhold the whole axis on a currency it never reported.
    selected.filter((s) => bars.some((b) => b.accountId === s.club.accountId)),
    def,
    // ⚠️⚠️ THE COST SIDE DROPS THE MONTH IN PROGRESS; THE BARS KEEP IT. A
    // partial count is a real partial count, hatched; a ratio over a partial
    // period is not a partial ratio but a wrong one, and one of them was
    // stretching the axis about fourfold.
    series.partialFinalBucket,
  );

  return {
    key: def.key,
    title: def.title,
    volumeLabel: def.volumeLabel,
    costLabel: def.costLabel,
    bars,
    volumeAxis: zeroBasedAxis(bars.flatMap((b) => b.values)),
    cost: cost.layer,
    costAxisWithheldReason: cost.axisWithheldReason,
    withheld: [
      ...withheld,
      ...cost.clubsWithheld.filter((c) => !withheld.some((w) => w.name === c.name)),
    ],
  };
}


// ---------------------------------------------------------------------------
// Which layers a card draws
// ---------------------------------------------------------------------------

/**
 * ⚠️⚠️ `Both — Bars — Lines`, NOT `Bar | Line` (§4d, 2026-09-03).
 *
 * The CRM cards' switch chooses HOW ONE SERIES IS DRAWN. This one chooses WHICH
 * OF TWO SERIES IS DRAWN, on different axes and in different units. Reusing that
 * control's vocabulary would say the two do the same thing, and a reader who
 * pressed "Bar" expecting a redrawn cost line would get its axis removed
 * instead.
 *
 * ⚠️ PER CARD, and DELIBERATELY NOT IN THE URL. It defines the VIEW rather than
 * the REPORT: two readers of the same link must see the same figures, and a
 * forwarded URL that hid half a chart would look like a different report. The
 * period, the comparison and the scope are in the URL precisely because they
 * change WHAT is reported.
 */
export type GroupChartForm = 'both' | 'bars' | 'lines';

export const GROUP_CHART_FORMS: ReadonlyArray<{ value: GroupChartForm; label: string }> = [
  { value: 'both', label: 'Both' },
  { value: 'bars', label: 'Bars' },
  { value: 'lines', label: 'Lines' },
];

export interface GroupChartRender {
  showBars: boolean;
  showCost: boolean;
  /** ⚠️ Why the chosen form has nothing to draw, or null. */
  emptyReason: string | null;
}

/**
 * What one card draws, given its form.
 *
 * ⚠️⚠️ THE ABSENT AXIS TAKES ITS GUTTER WITH IT, which is why this returns
 * booleans the renderer reads rather than a mutated model. `Bars` reuses the
 * mixed-currency path exactly — no `CostLayer`, no left axis, no left margin —
 * so there is one way for a cost axis to be absent rather than two.
 *
 * ⚠️⚠️ AND `Lines` MUST NOT LOOK LIKE "no data". `bars: []` would trip the
 * renderer's own empty state and print "None of the selected locations has
 * figures to draw", which is a false statement about the data rather than a
 * description of the chosen view. So the model is never emptied; the layer is
 * switched off and the reason, when there is one, says which.
 */
export function resolveChartForm(
  chart: GroupChartModel,
  form: GroupChartForm,
): GroupChartRender {
  if (chart.bars.length === 0) {
    return {
      showBars: false,
      showCost: false,
      emptyReason: 'None of the selected locations has figures to draw on this chart.',
    };
  }
  if (form === 'lines' && chart.cost === null) {
    // ⚠️ The WHY is already stated above the chart (`costAxisWithheldReason`),
    // so this says only that the chosen view is empty — repeating a currency
    // explanation here would read as a second, different problem.
    return {
      showBars: false,
      showCost: false,
      emptyReason: 'There is no cost line to show, so this view is empty. Choose Bars or Both.',
    };
  }
  return {
    showBars: form !== 'lines',
    showCost: form !== 'bars' && chart.cost !== null,
    emptyReason: null,
  };
}

/**
 * Which side of the plot the tooltip sits on.
 *
 * ⚠️⚠️ THE TOOLTIP USED TO COVER THE COLUMN IT DESCRIBED. It was anchored to
 * the cursor's x and the SVG's top edge with a 10px drop, which is fine for the
 * trend chart's two rows and wrong here: this one lists EVERY ticked club, so at
 * eight clubs it is a ~150px box landing squarely on the month being read.
 *
 * ⚠️ SO IT FLIPS TO THE FAR SIDE OF THE PLOT. The hovered column is never
 * covered, the box stays inside the card horizontally (no viewport-flip logic to
 * get wrong), and the highlighted band still says which month it describes.
 *
 * ⚠️ IT KEEPS THE SVG'S TOP EDGE as its vertical anchor, which is the trend
 * chart's own measured decision: the control row is a SIBLING ABOVE the SVG, so
 * anchoring there makes overlapping the controls impossible without measuring
 * anything.
 */
export function tooltipSide(index: number, bucketCount: number): 'left' | 'right' {
  return index < bucketCount / 2 ? 'right' : 'left';
}

/**
 * The margin the group tooltip keeps between itself and the viewport edge, in px.
 */
export const TIP_VIEWPORT_MARGIN = 12;

/**
 * How wide the group tooltip may be, in px.
 *
 * ⚠️⚠️ TWO CEILINGS, WHICHEVER BINDS (round 8 §4, 2026-09-04). The PLOT's width
 * is the "no wider than the card" half. The VIEWPORT's is the half that was
 * MEASURED FAILING: the box is CENTRED on its anchor (78% / 22% of the plot, see
 * `tooltipSide`), so round 7 sizing it to its content moved its far edge OFF
 * SCREEN at narrow desktop widths — at 1024×800 with the 232px rail present, an
 * eight-club box measured 503px and its right edge landed at 1074, 50px past the
 * viewport.
 *
 * ⚠️ CARD OVERHANG IS ACCEPTED (round 7 decision 1): a tooltip is a floating
 * overlay and overhanging its container is conventional. Leaving the VIEWPORT is
 * different — that is content made unreachable.
 *
 * ⚠️⚠️ COMPUTED, NOT A `vw` CONSTANT — a deviation from the brief, and measured
 * rather than argued. A proportional ceiling cannot be right at every width
 * because the containing ratio depends on the plot's LEFT edge: `38vw` cleared
 * 1024 by 7px and 900 by ONE PIXEL, and `vw` INCLUDES THE SCROLLBAR (measured: a
 * 1024 window has a 1009px layout viewport), so even that pixel was notional.
 * ⚠️ It is also STRICTLY BETTER, not merely safer: a `vw` cap binds on BOTH
 * anchors, so it clipped eight club names on the LEFT side where there was 150px
 * of room to spare. This binds only on the side that needs it.
 *
 * ⚠️ STILL NO LAYOUT EFFECT AND NO SECOND PASS, which is what the brief's
 * constraint was protecting: the box's OWN width is never measured. Both inputs
 * are already in hand where this is called.
 *
 * ⚠️ The viewport width is a PARAMETER, so this is pure and testable without a
 * DOM — the `nowMs`-as-an-argument convention. Callers pass
 * `documentElement.clientWidth`, NOT `window.innerWidth`: the former excludes
 * the vertical scrollbar, and the report always has one.
 */
export function tooltipCeiling(
  tipX: number,
  plotWidth: number,
  viewportWidth: number,
): number {
  const room = Math.min(tipX, viewportWidth - tipX) - TIP_VIEWPORT_MARGIN;
  // ⚠️ `max(0, …)` so a pathological anchor cannot yield a NEGATIVE ceiling,
  // which CSS ignores — leaving the box uncapped, i.e. the bug this prevents.
  return Math.min(plotWidth, Math.max(0, room * 2));
}

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

/**
 * ⚠️⚠️ THE CHART NAMES THE CLUBS IT IS DRAWING.
 *
 * The chart's contents now change with the chips, so two owners can be looking
 * at the same report and see different lines. That is a defensible trade for an
 * owner-driven snapshot — but only if the chart says whose figures are on it,
 * rather than relying on the reader remembering what they ticked.
 */
export function clubsDrawnNote(bars: readonly BarSeries[]): string | null {
  if (bars.length === 0) return null;
  const names = bars.map((b) => b.name);
  if (names.length === 1) return `Showing ${names[0]}.`;
  return `Showing ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}.`;
}

/** ⚠️ Said once, where the clubs are chosen, rather than left to be discovered. */
export function excludedClubsNote(excluded: readonly PortalGroupClub[]): string | null {
  if (excluded.length === 0) return null;
  const names = excluded.map((c) => c.name).join(', ');
  return `${excluded.length} ${excluded.length === 1 ? 'location is' : 'locations are'} not on these charts — ${names}. Eight is the most that can be told apart by colour; their figures are in the table above and on their own report.`;
}

/**
 * ⚠️⚠️ THE PARTIAL FINAL MONTH, NAMED — AND SCOTT'S OWN EXAMPLE CONTAINS THE BUG
 * THIS PREVENTS. In that screenshot September is an unmarked near-zero sliver in
 * both charts: "every series collapsing at the September edge, which reads as a
 * business-wide crash" (`groupSeries.ts`). The month is KEPT and MARKED — copy
 * the chart, not that.
 */
export function partialBucketNote(series: PortalGroupSeries): string | null {
  if (!series.partialFinalBucket) return null;
  const last = series.buckets[series.buckets.length - 1];
  return last
    ? `${last.label} is still in progress, so its bars and cost are lower than a full month — it is hatched on each chart.`
    : null;
}

/**
 * ⚠️ The band's own framing sentence. It replaces round 4's index explanation,
 * which described a chart that no longer exists.
 */
export const GROUP_CHARTS_INTRO =
  'The last 13 months for the locations you tick below. Bars are the monthly count on the right axis; the line is the cost per one, on the left. Both axes start at zero.';
